import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { es } from "date-fns/locale";
import {
  BarChart, ComposedChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell, Legend,
} from "recharts";
import useStore from "../../../useStore";
import { useAuthentication } from "../../../Hooks/useAuthentication";
import { useToast } from "../../../Contexts/Toast/toast.provider";
import Loader from "../../Elements/Loader/Loader";
import { canViewForms } from "../../../Constants/roles";
import { getProcesoColor, getProcesoDailyGoal, getPeriodDays, getPeriodGoalLabel } from "../../../Constants/procesos";
import "./stats.css";

const PERIODS = [
  { key: "day",   label: "Día" },
  { key: "week",  label: "Semana" },
  { key: "month", label: "Mes" },
];

const VIEWS = [
  { key: "general",  label: "General" },
  { key: "turno",    label: "Por turno" },
  { key: "persona",  label: "Por persona" },
];

const TURNOS = ["1er", "2do"];

const toISO = (f) => {
  const raw = String(f?.fecha || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{2}-\d{2}-\d{2,4}$/.test(raw)) {
    const [dd, mm, yy] = raw.split("-");
    return `${yy.length === 2 ? "20" + yy : yy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  }
  return f?.createdAt ? String(f.createdAt).slice(0, 10) : "";
};

const dateToISO = (d) => d.toISOString().slice(0, 10);

const getWeekRange = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const start = new Date(d);
  start.setDate(d.getDate() + diff);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start: dateToISO(start), end: dateToISO(end) };
};

const getMonthRange = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const last = new Date(yyyy, date.getMonth() + 1, 0).getDate();
  return { start: `${yyyy}-${mm}-01`, end: `${yyyy}-${mm}-${last}` };
};

// Meses (YYYY-MM) que hay que pedir al backend para cubrir el periodo.
// Normalmente uno; dos si la semana cruza de un mes a otro.
const getPeriodMonths = (period, date) => {
  if (period === "week") {
    const { start, end } = getWeekRange(date);
    return Array.from(new Set([start.slice(0, 7), end.slice(0, 7)]));
  }
  return [getMonthRange(date).start.slice(0, 7)];
};

const filterByPeriod = (list, period, selectedDate) => {
  if (period === "day") {
    const day = dateToISO(selectedDate);
    return list.filter((f) => toISO(f) === day);
  }
  if (period === "week") {
    const { start, end } = getWeekRange(selectedDate);
    return list.filter((f) => { const d = toISO(f); return d >= start && d <= end; });
  }
  if (period === "month") {
    const { start, end } = getMonthRange(selectedDate);
    return list.filter((f) => { const d = toISO(f); return d >= start && d <= end; });
  }
  return list;
};

const formatPeriodLabel = (period, date) => {
  if (period === "day") return dateToISO(date);
  if (period === "week") {
    const { start, end } = getWeekRange(date);
    return `${start} al ${end}`;
  }
  if (period === "month") {
    return date.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  }
  return "";
};

const groupBy = (list, key) => {
  const map = {};
  list.forEach((f) => {
    const k = String(f?.[key] || "").trim() || "Sin dato";
    const n = Number(f?.totalEntregadas);
    if (!map[k]) map[k] = { name: k, piezas: 0 };
    map[k].piezas += Number.isFinite(n) ? n : 0;
  });
  return Object.values(map).sort((a, b) => b.piezas - a.piezas);
};

// Igual que groupBy(list, "proceso") pero además agrega:
// - fill: color fijo del proceso (mismo en todas las gráficas)
// - meta: meta de piezas del proceso ya escalada al periodo (o null si el
//   proceso no tiene meta definida)
const groupByProcesoConMeta = (list, periodDays) => {
  return groupBy(list, "proceso").map((row) => {
    const goal = getProcesoDailyGoal(row.name);
    return {
      ...row,
      fill: getProcesoColor(row.name),
      meta: goal != null ? goal * periodDays : null,
    };
  });
};

// Pivotea la lista a: [{ name: usuario, [proceso1]: piezas, [proceso2]: piezas, ... }]
// para poder apilar por proceso en el chart "Quién entrega más" y así ver,
// para alguien que trabajó en más de un proceso, cuánto corresponde a cada uno.
const groupByUsuarioProceso = (list) => {
  const rowsMap = {};
  const procesoSet = new Set();

  list.forEach((f) => {
    const user = f?.submittedBy?.username || "Sin dato";
    const proceso = String(f?.proceso || "").trim() || "Sin dato";
    const n = Number(f?.totalEntregadas);
    const piezas = Number.isFinite(n) ? n : 0;

    if (!rowsMap[user]) rowsMap[user] = { name: user, __total: 0 };
    rowsMap[user][proceso] = (rowsMap[user][proceso] || 0) + piezas;
    rowsMap[user].__total += piezas;
    procesoSet.add(proceso);
  });

  const rows = Object.values(rowsMap).sort((a, b) => b.__total - a.__total);
  const procesos = Array.from(procesoSet).sort();
  return { rows, procesos };
};

const tooltipFormatter = (v, n) => [Number(v).toLocaleString("es-MX"), n];

// Marca de meta: una línea horizontal corta centrada sobre su propia barra
// (no un punto, y no conectada con la meta de otro proceso — cada una es
// independiente). Se dibuja vía el `dot` de un <Line> con stroke="none"
// (así no aparece ninguna diagonal uniendo procesos distintos).
const GoalTick = ({ cx, cy, payload }) => {
  if (cx == null || cy == null || payload?.meta == null) return null;
  return (
    <line
      x1={cx - 16}
      x2={cx + 16}
      y1={cy}
      y2={cy}
      stroke="#212529"
      strokeWidth={3}
      strokeLinecap="round"
    />
  );
};

// Chart "Por proceso": una barra por proceso (color fijo, ver
// Constants/procesos.js). Cuando `showGoal` es true además dibuja la meta de
// ese proceso (ya escalada al periodo) como una línea horizontal sobre su
// propia barra — las metas son PERSONALES, así que esto solo debe usarse en
// la vista "Por persona" (datos ya filtrados a un solo usuario); en
// General/Por turno el total de un proceso suma a varias personas y no es
// comparable contra una meta individual.
const ProcesoChart = ({ data, goalName, showGoal = false, height = 260, marginBottom = 40 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <ComposedChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: marginBottom }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
      <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 700 }} angle={-35} textAnchor="end" interval={0} />
      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
      <Tooltip formatter={tooltipFormatter} />
      {showGoal && <Legend wrapperStyle={{ fontSize: 12 }} />}
      <Bar dataKey="piezas" name="Piezas entregadas" radius={[6, 6, 0, 0]}>
        {data.map((entry) => (
          <Cell key={entry.name} fill={entry.fill} />
        ))}
      </Bar>
      {showGoal && (
        <Line
          dataKey="meta"
          name={goalName}
          stroke="#212529"
          strokeWidth={0}
          isAnimationActive={false}
          dot={(props) => <GoalTick key={`goal-${props.payload?.name}`} {...props} />}
          activeDot={(props) => <GoalTick key={`goal-active-${props.payload?.name}`} {...props} />}
        />
      )}
    </ComposedChart>
  </ResponsiveContainer>
);

// Chart "Quién entrega más": una barra apilada por usuario, con un segmento
// por proceso (mismo color fijo en toda la página) — así se ve, para quien
// trabajó en más de un proceso, cuánto le corresponde a cada uno.
const UsuarioProcesoChart = ({ rows, procesos, height = 280 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <BarChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
      <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 700 }} angle={-35} textAnchor="end" interval={0} />
      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
      <Tooltip formatter={tooltipFormatter} />
      <Legend wrapperStyle={{ fontSize: 12 }} />
      {procesos.map((p, i) => (
        <Bar
          key={p}
          dataKey={p}
          name={p}
          stackId="proceso"
          fill={getProcesoColor(p)}
          radius={i === procesos.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
        />
      ))}
    </BarChart>
  </ResponsiveContainer>
);

const Stats = () => {
  const showToast = useToast();
  const { isAuthenticated, role } = useAuthentication();
  const { fetchDataGetFormsByMonths, forms, formsLoading: loading } = useStore();

  const [view, setView]               = useState("general");
  const [period, setPeriod]           = useState("month");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedTurno, setSelectedTurno] = useState("");
  const [selectedProceso, setSelectedProceso] = useState("");

  const canView = canViewForms(role);

  // ✅ solo se piden los meses del periodo seleccionado; traer la tabla
  // completa rebasaba el límite de 6MB de respuesta del Lambda
  const monthsKey = getPeriodMonths(period, selectedDate).join(",");

  const load = useCallback(async () => {
    try {
      await fetchDataGetFormsByMonths(monthsKey.split(","));
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Error al cargar datos");
    }
  }, [fetchDataGetFormsByMonths, monthsKey, showToast]);

  useEffect(() => {
    if (isAuthenticated && canView) load();
  }, [isAuthenticated, canView, monthsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const allForms = useMemo(() => (Array.isArray(forms) ? forms : []), [forms]);
  const list     = useMemo(() => filterByPeriod(allForms, period, selectedDate), [allForms, period, selectedDate]);

  // ✅ para escalar la meta diaria por proceso al periodo seleccionado
  // (semana = ×7, mes = × días del mes).
  const periodDays = useMemo(() => getPeriodDays(period, selectedDate), [period, selectedDate]);
  const goalLabel = useMemo(() => getPeriodGoalLabel(period, periodDays), [period, periodDays]);

  const totalEntregadas = useMemo(() =>
    list.reduce((acc, f) => {
      const n = Number(f?.totalEntregadas);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0), [list]);

  const byTurno   = useMemo(() => groupBy(list, "turno"),  [list]);
  const byProceso = useMemo(() => groupByProcesoConMeta(list, periodDays), [list, periodDays]);

  const byUser = useMemo(() => {
    const map = {};
    list.forEach((f) => {
      const key = f?.submittedBy?.username || "Sin dato";
      const n = Number(f?.totalEntregadas);
      if (!map[key]) map[key] = { name: key, piezas: 0 };
      map[key].piezas += Number.isFinite(n) ? n : 0;
    });
    return Object.values(map).sort((a, b) => b.piezas - a.piezas);
  }, [list]);

  // ✅ mismo total que byUser, pero desglosado por proceso (para apilar por
  // color en "Quién entrega más" y ver qué corresponde a cada proceso).
  const byUserProceso = useMemo(() => groupByUsuarioProceso(list), [list]);

  const byFecha = useMemo(() => {
    const map = {};
    list.forEach((f) => {
      const fecha = toISO(f);
      if (!fecha) return;
      const n = Number(f?.totalEntregadas);
      map[fecha] = (map[fecha] || 0) + (Number.isFinite(n) ? n : 0);
    });
    return Object.entries(map)
      .map(([fecha, total]) => ({ fecha, total }))
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [list]);

  // Usa allForms (todo el mes o meses traídos, sin filtro de periodo) para listar usuarios
  const userOptions = useMemo(() => {
    const set = new Set();
    allForms.forEach((f) => { if (f?.submittedBy?.username) set.add(f.submittedBy.username); });
    return Array.from(set).sort();
  }, [allForms]);

  const userForms = useMemo(() =>
    selectedUser ? list.filter((f) => f?.submittedBy?.username === selectedUser) : [],
    [list, selectedUser]
  );

  const userJourney = useMemo(() => {
    if (!userForms.length) return [];
    if (period === "day") return groupByProcesoConMeta(userForms, periodDays);
    const map = {};
    userForms.forEach((f) => {
      const fecha = toISO(f);
      if (!fecha) return;
      const n = Number(f?.totalEntregadas);
      map[fecha] = (map[fecha] || 0) + (Number.isFinite(n) ? n : 0);
    });
    return Object.entries(map)
      .map(([name, piezas]) => ({ name, piezas }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [userForms, period, periodDays]);

  const userByProceso = useMemo(() => groupByProcesoConMeta(userForms, periodDays), [userForms, periodDays]);
  const userByTurno   = useMemo(() => groupBy(userForms, "turno"),   [userForms]);

  const userTotal = useMemo(() =>
    userJourney.reduce((acc, r) => acc + r.piezas, 0), [userJourney]);

  // ── POR TURNO ──
  const turnoList = useMemo(() =>
    selectedTurno ? list.filter((f) => f?.turno === selectedTurno) : list,
    [list, selectedTurno]
  );

  const procesoOptions = useMemo(() => {
    const set = new Set();
    turnoList.forEach((f) => { if (f?.proceso) set.add(String(f.proceso).trim()); });
    return Array.from(set).sort();
  }, [turnoList]);

  const turnoProcesoList = useMemo(() =>
    selectedProceso ? turnoList.filter((f) => String(f?.proceso || "").trim() === selectedProceso) : turnoList,
    [turnoList, selectedProceso]
  );

  const turnoByProceso = useMemo(
    () => groupByProcesoConMeta(turnoProcesoList, periodDays),
    [turnoProcesoList, periodDays]
  );

  const turnoByUserFixed = useMemo(() => {
    const map = {};
    turnoProcesoList.forEach((f) => {
      const k = f?.submittedBy?.username || "Sin dato";
      const n = Number(f?.totalEntregadas);
      if (!map[k]) map[k] = { name: k, piezas: 0 };
      map[k].piezas += Number.isFinite(n) ? n : 0;
    });
    return Object.values(map).sort((a, b) => b.piezas - a.piezas);
  }, [turnoProcesoList]);

  // ✅ igual que turnoByUserFixed, pero desglosado por proceso para apilar.
  const turnoByUserProceso = useMemo(() => groupByUsuarioProceso(turnoProcesoList), [turnoProcesoList]);

  const turnoByFecha = useMemo(() => {
    const map = {};
    turnoProcesoList.forEach((f) => {
      const fecha = toISO(f);
      if (!fecha) return;
      const n = Number(f?.totalEntregadas);
      map[fecha] = (map[fecha] || 0) + (Number.isFinite(n) ? n : 0);
    });
    return Object.entries(map)
      .map(([fecha, total]) => ({ fecha, total }))
      .sort((a, b) => a.fecha.localeCompare(b.fecha));
  }, [turnoProcesoList]);

  const turnoTotal = useMemo(() =>
    turnoProcesoList.reduce((acc, f) => {
      const n = Number(f?.totalEntregadas);
      return acc + (Number.isFinite(n) ? n : 0);
    }, 0), [turnoProcesoList]);

  const exportTurno = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const add = (name, data) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), name);
    add("Por proceso", turnoByProceso.map((r) => ({ "Proceso": r.name, "Piezas entregadas": r.piezas })));
    add("Por usuario", turnoByUserFixed.map((r) => ({ "Usuario": r.name, "Piezas entregadas": r.piezas })));
    add("Por día",     turnoByFecha.map((r)    => ({ "Fecha":   r.fecha, "Piezas entregadas": r.total })));
    const sufijo = [selectedTurno, selectedProceso].filter(Boolean).join("_") || "todos";
    XLSX.writeFile(wb, `turno_${sufijo}_${period}_${dateToISO(selectedDate)}.xlsx`);
  }, [turnoByProceso, turnoByUserFixed, turnoByFecha, selectedTurno, selectedProceso, period, selectedDate]);

  const exportGeneral = useCallback(() => {
    const wb = XLSX.utils.book_new();
    const add = (name, data) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), name);
    add("Por turno",   byTurno.map((r)   => ({ "Turno":   r.name, "Piezas entregadas": r.piezas })));
    add("Por proceso", byProceso.map((r) => ({ "Proceso": r.name, "Piezas entregadas": r.piezas })));
    add("Por usuario", byUser.map((r)    => ({ "Usuario": r.name, "Piezas entregadas": r.piezas })));
    add("Por día",     byFecha.map((r)   => ({ "Fecha":   r.fecha, "Piezas entregadas": r.total  })));
    XLSX.writeFile(wb, `general_${period}_${dateToISO(selectedDate)}.xlsx`);
  }, [byTurno, byProceso, byUser, byFecha, period, selectedDate]);

  const exportPersona = useCallback(() => {
    if (!selectedUser || !userJourney.length) return;
    const wb = XLSX.utils.book_new();
    const add = (name, data) => XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), name);
    const col = period === "day" ? "Proceso" : "Fecha";
    add("Recorrido",   userJourney.map((r)   => ({ [col]:      r.name, "Piezas entregadas": r.piezas })));
    add("Por proceso", userByProceso.map((r) => ({ "Proceso":  r.name, "Piezas entregadas": r.piezas })));
    add("Por turno",   userByTurno.map((r)   => ({ "Turno":    r.name, "Piezas entregadas": r.piezas })));
    XLSX.writeFile(wb, `recorrido_${selectedUser}_${period}_${dateToISO(selectedDate)}.xlsx`);
  }, [userJourney, userByProceso, userByTurno, selectedUser, period, selectedDate]);

  if (!isAuthenticated || !canView) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">No tienes permisos para ver esta sección.</div>
      </div>
    );
  }

  if (loading) return <Loader />;

  const empty = list.length === 0;
  const periodLabel = formatPeriodLabel(period, selectedDate);

  return (
    <div className="stats-page">
      <div className="container py-5">

        {/* Header */}
        <div className="stats-header">
          <div>
            <h1 className="stats-title">Indicadores</h1>
            <p className="stats-subtitle">
              {empty
                ? `Sin registros — ${periodLabel}`
                : `${list.length} registro${list.length !== 1 ? "s" : ""} — ${periodLabel}`}
            </p>
          </div>

          <div className="stats-header-actions">
            {/* View tabs */}
            <div className="stats-period-tabs">
              {VIEWS.map((v) => (
                <button
                  key={v.key}
                  className={`stats-period-btn ${view === v.key ? "active" : ""}`}
                  onClick={() => setView(v.key)}
                >
                  {v.label}
                </button>
              ))}
            </div>

            <div className="stats-divider" />

            {/* Period tabs */}
            <div className="stats-period-tabs">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  className={`stats-period-btn ${period === p.key ? "active" : ""}`}
                  onClick={() => setPeriod(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {period === "day" && (
              <DatePicker selected={selectedDate} onChange={setSelectedDate} locale={es}
                dateFormat="dd/MM/yyyy" className="stats-datepicker" placeholderText="Selecciona día" />
            )}
            {period === "week" && (
              <DatePicker selected={selectedDate} onChange={setSelectedDate} locale={es}
                dateFormat="'Semana del' dd/MM/yyyy" className="stats-datepicker"
                placeholderText="Selecciona semana" showWeekNumbers calendarStartDay={1} />
            )}
            {period === "month" && (
              <DatePicker selected={selectedDate} onChange={setSelectedDate} locale={es}
                dateFormat="MMMM yyyy" className="stats-datepicker"
                showMonthYearPicker placeholderText="Selecciona mes" />
            )}

            <button className="btn stats-refresh-btn" onClick={load}>Refrescar</button>
          </div>
        </div>

        {/* KPIs siempre visibles */}
        <div className="stats-kpis">
          <div className="stats-kpi">
            <div className="stats-kpi-label">Registros</div>
            <div className="stats-kpi-value">{list.length}</div>
          </div>
          <div className="stats-kpi">
            <div className="stats-kpi-label">Total entregadas</div>
            <div className="stats-kpi-value">{totalEntregadas.toLocaleString("es-MX")}</div>
            <div className="stats-kpi-sub">según folios</div>
          </div>
        </div>

        {empty ? (
          <div className="alert alert-info">No hay formularios para {periodLabel}.</div>
        ) : (
          <>
            {/* ── VISTA GENERAL ── */}
            {view === "general" && (
              <>
                <div className="stats-view-header">
                  <span className="stats-view-label">Indicadores generales</span>
                  <button className="btn stats-export-btn" onClick={exportGeneral}>
                    Exportar Excel
                  </button>
                </div>

                <div className="stats-grid">
                  <div className="stats-card">
                    <div className="stats-card-title">Por turno</div>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={byTurno} margin={{ top: 8, right: 16, left: 0, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
                        <XAxis dataKey="name" tick={{ fontSize: 13, fontWeight: 700 }} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => [v.toLocaleString("es-MX"), "Piezas entregadas"]} />
                        <Bar dataKey="piezas" name="Piezas entregadas" radius={[6, 6, 0, 0]} fill="#20c997" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="stats-card">
                    <div className="stats-card-title">Por proceso</div>
                    <ProcesoChart data={byProceso} height={260} marginBottom={40} />
                  </div>

                  <div className="stats-card stats-card-wide">
                    <div className="stats-card-title">Quién entrega más</div>
                    <UsuarioProcesoChart rows={byUserProceso.rows} procesos={byUserProceso.procesos} height={280} />
                  </div>

                  <div className="stats-card stats-card-full">
                    <div className="stats-card-title">Total entregadas por día</div>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={byFecha} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => [v.toLocaleString("es-MX"), "Entregadas"]} />
                        <Line type="monotone" dataKey="total" name="Entregadas"
                          stroke="#0d6efd" strokeWidth={3}
                          dot={{ r: 4, fill: "#0d6efd" }} activeDot={{ r: 7 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}

            {/* ── VISTA POR TURNO ── */}
            {view === "turno" && (
              <>
                <div className="stats-view-header">
                  <span className="stats-view-label">Indicadores por turno</span>
                  <div className="stats-view-header-actions">
                    {/* Selector turno */}
                    <select
                      className="stats-user-select"
                      value={selectedTurno}
                      onChange={(e) => { setSelectedTurno(e.target.value); setSelectedProceso(""); }}
                    >
                      <option value="">— Todos los turnos —</option>
                      {TURNOS.map((t) => (
                        <option key={t} value={t}>{t} turno</option>
                      ))}
                    </select>

                    {/* Selector proceso (depende del turno) */}
                    <select
                      className="stats-user-select"
                      value={selectedProceso}
                      onChange={(e) => setSelectedProceso(e.target.value)}
                    >
                      <option value="">— Todos los procesos —</option>
                      {procesoOptions.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>

                    <button className="btn stats-export-btn" onClick={exportTurno}>
                      Exportar Excel
                    </button>
                  </div>
                </div>

                {/* KPI del filtro */}
                <div className="stats-kpis" style={{ marginBottom: 18 }}>
                  <div className="stats-kpi">
                    <div className="stats-kpi-label">Registros filtrados</div>
                    <div className="stats-kpi-value">{turnoProcesoList.length}</div>
                  </div>
                  <div className="stats-kpi">
                    <div className="stats-kpi-label">Total entregadas</div>
                    <div className="stats-kpi-value">{turnoTotal.toLocaleString("es-MX")}</div>
                    <div className="stats-kpi-sub">
                      {[selectedTurno && `${selectedTurno} turno`, selectedProceso].filter(Boolean).join(" · ") || "todos"}
                    </div>
                  </div>
                </div>

                <div className="stats-grid">
                  <div className="stats-card">
                    <div className="stats-card-title">Por proceso</div>
                    <ProcesoChart data={turnoByProceso} height={260} marginBottom={40} />
                  </div>

                  <div className="stats-card">
                    <div className="stats-card-title">Quién entrega más</div>
                    <UsuarioProcesoChart rows={turnoByUserProceso.rows} procesos={turnoByUserProceso.procesos} height={260} />
                  </div>

                  <div className="stats-card stats-card-full">
                    <div className="stats-card-title">Total entregadas por día</div>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={turnoByFecha} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
                        <XAxis dataKey="fecha" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => [v.toLocaleString("es-MX"), "Entregadas"]} />
                        <Line type="monotone" dataKey="total" name="Entregadas"
                          stroke="#fd7e14" strokeWidth={3}
                          dot={{ r: 4, fill: "#fd7e14" }} activeDot={{ r: 7 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </>
            )}

            {/* ── VISTA POR PERSONA ── */}
            {view === "persona" && (
              <>
                <div className="stats-view-header">
                  <span className="stats-view-label">Recorrido por persona</span>
                  <div className="stats-view-header-actions">
                    <select
                      className="stats-user-select"
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                    >
                      <option value="">— Selecciona un usuario —</option>
                      {userOptions.map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                    {selectedUser && userJourney.length > 0 && (
                      <button className="btn stats-export-btn" onClick={exportPersona}>
                        Exportar Excel
                      </button>
                    )}
                  </div>
                </div>

                {!selectedUser ? (
                  <div className="stats-user-empty">Selecciona un usuario para ver su recorrido.</div>
                ) : userJourney.length === 0 ? (
                  <div className="stats-user-empty">Sin registros para <b>{selectedUser}</b> en este periodo.</div>
                ) : (
                  <div className="stats-grid">
                    {/* Recorrido */}
                    <div className="stats-card stats-card-full">
                      <div className="stats-user-total">
                        Total de <b>{selectedUser}</b> — {periodLabel}:{" "}
                        <span>{userTotal.toLocaleString("es-MX")} piezas</span>
                      </div>
                      <div className="stats-card-title" style={{ marginTop: 8 }}>
                        {period === "day" ? "Piezas por proceso" : "Piezas por día"}
                      </div>
                      {period === "day" ? (
                        <ProcesoChart data={userJourney} goalName={goalLabel} showGoal height={280} marginBottom={40} />
                      ) : (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={userJourney} margin={{ top: 8, right: 16, left: 0, bottom: 40 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
                            <XAxis dataKey="name" tick={{ fontSize: 12, fontWeight: 700 }} angle={-35} textAnchor="end" interval={0} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                            <Tooltip formatter={tooltipFormatter} />
                            <Bar dataKey="piezas" name="Piezas entregadas" radius={[6, 6, 0, 0]} fill="#6f42c1" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>

                    {/* Por proceso */}
                    <div className="stats-card">
                      <div className="stats-card-title">Por proceso</div>
                      <ProcesoChart data={userByProceso} goalName={goalLabel} showGoal height={260} marginBottom={40} />
                    </div>

                    {/* Por turno */}
                    <div className="stats-card">
                      <div className="stats-card-title">Por turno</div>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={userByTurno} margin={{ top: 8, right: 16, left: 0, bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,.06)" />
                          <XAxis dataKey="name" tick={{ fontSize: 13, fontWeight: 700 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                          <Tooltip formatter={(v) => [v.toLocaleString("es-MX"), "Piezas entregadas"]} />
                          <Bar dataKey="piezas" name="Piezas entregadas" radius={[6, 6, 0, 0]} fill="#6f42c1" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Stats;
